<template>
  <div class="model-page">
    <div class="page-header">
      <h2 class="page-title">模型管理</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">产品名称</span>
        <el-input
          v-model="productName"
          size="small"
          placeholder="请输入产品名称"
          clearable
          style="width: 160px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">核算标准</span>
        <el-select v-model="standard" size="small" clearable placeholder="全部" style="width: 220px">
          <el-option v-for="item in standards" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">模型状态</span>
        <el-select v-model="status" size="small" clearable placeholder="全部" style="width: 120px">
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row stat-cards-3">
      <stat-card
        title="模型总数"
        :today="summary.total.today"
        :annual="summary.total.annual"
        :unit="summary.total.unit"
        :annual-unit="summary.total.unit"
        icon="el-icon-s-operation"
        color="#409EFF"
      />
      <stat-card
        title="已发布"
        :today="summary.published.today"
        :annual="summary.published.annual"
        :unit="summary.published.unit"
        :annual-unit="summary.published.unit"
        icon="el-icon-circle-check"
        color="#67C23A"
      />
      <stat-card
        title="编制中"
        :today="summary.draft.today"
        :annual="summary.draft.annual"
        :unit="summary.draft.unit"
        :annual-unit="summary.draft.unit"
        icon="el-icon-edit-outline"
        color="#E6A23C"
      />
    </div>

    <div class="table-section">
      <div class="table-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openDialog()">
          新建模型
        </el-button>
      </div>
      <el-table :data="models" border stripe size="small">
        <el-table-column prop="name" label="模型名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="productName" label="关联产品" min-width="160" show-overflow-tooltip />
        <el-table-column prop="functionalUnit" label="功能单位" width="100" />
        <el-table-column prop="standard" label="核算标准" width="200" show-overflow-tooltip />
        <el-table-column prop="nodeCount" label="节点数" width="80" align="center" />
        <el-table-column prop="statusLabel" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 'published' ? 'success' : 'warning'" size="mini">
              {{ row.statusLabel }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updatedAt" label="更新时间" width="120" />
        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="goDetail(row)">查看详情</el-button>
            <el-button type="text" size="small" @click="openDialog(row)">编辑</el-button>
            <el-button type="text" size="small" class="text-danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="520px"
      @close="resetForm"
    >
      <el-form ref="modelForm" :model="modelForm" :rules="formRules" label-width="100px" size="small">
        <el-form-item label="模型名称" prop="name">
          <el-input v-model="modelForm.name" placeholder="请输入模型名称" />
        </el-form-item>
        <el-form-item label="关联产品" prop="productName">
          <el-input v-model="modelForm.productName" placeholder="请输入产品名称" />
        </el-form-item>
        <el-form-item label="功能单位" prop="functionalUnit">
          <el-input v-model="modelForm.functionalUnit" placeholder="如 1件" />
        </el-form-item>
        <el-form-item label="核算标准" prop="standard">
          <el-select v-model="modelForm.standard" placeholder="请选择" style="width: 100%">
            <el-option v-for="item in standards" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="模型状态" prop="status">
          <el-select v-model="modelForm.status" placeholder="请选择" style="width: 100%">
            <el-option
              v-for="item in statusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveModel">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import { getModelListData } from '@/api/modules/carbonFootprint'

const emptyForm = () => ({
  id: null,
  name: '',
  productName: '',
  functionalUnit: '',
  standard: '',
  status: 'draft',
  nodeCount: 0
})

export default {
  name: 'CarbonFootprintModel',
  components: { FilterBar, StatCard },
  data() {
    return {
      productName: '',
      standard: '',
      status: '',
      standards: [],
      statusOptions: [],
      summary: {
        total: { today: 0, annual: 0, unit: '个' },
        published: { today: 0, annual: 0, unit: '个' },
        draft: { today: 0, annual: 0, unit: '个' }
      },
      models: [],
      allModels: [],
      dialogVisible: false,
      dialogTitle: '新建模型',
      modelForm: emptyForm(),
      formRules: {
        name: [{ required: true, message: '请输入模型名称', trigger: 'blur' }],
        productName: [{ required: true, message: '请输入关联产品', trigger: 'blur' }],
        functionalUnit: [{ required: true, message: '请输入功能单位', trigger: 'blur' }],
        standard: [{ required: true, message: '请选择核算标准', trigger: 'change' }],
        status: [{ required: true, message: '请选择模型状态', trigger: 'change' }]
      },
      nextId: 100
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      const res = await getModelListData({
        productName: this.productName,
        standard: this.standard,
        status: this.status
      })
      const data = res.data
      this.standards = data.standards
      this.statusOptions = data.statusOptions
      if (!this.allModels.length) {
        this.allModels = data.models.map(item => ({ ...item }))
      }
      this.applyFilters()
    },
    applyFilters() {
      let list = [...this.allModels]
      if (this.productName) {
        const kw = this.productName.trim()
        list = list.filter(item => item.productName.includes(kw) || item.name.includes(kw))
      }
      if (this.standard) {
        list = list.filter(item => item.standard === this.standard)
      }
      if (this.status) {
        list = list.filter(item => item.status === this.status)
      }
      this.models = list
      this.updateSummary(list)
    },
    updateSummary(list) {
      const published = list.filter(item => item.status === 'published').length
      const draft = list.filter(item => item.status === 'draft').length
      this.summary = {
        total: { today: list.length, annual: list.length, unit: '个' },
        published: { today: published, annual: published, unit: '个' },
        draft: { today: draft, annual: draft, unit: '个' }
      }
    },
    getStatusLabel(status) {
      const item = this.statusOptions.find(s => s.value === status)
      return item ? item.label : status
    },
    goDetail(row) {
      this.$router.push({ path: '/carbon-footprint/model/detail', query: { id: row.id } })
    },
    openDialog(row) {
      if (row) {
        this.dialogTitle = '编辑模型'
        this.modelForm = { ...row }
      } else {
        this.dialogTitle = '新建模型'
        this.modelForm = emptyForm()
      }
      this.dialogVisible = true
    },
    resetForm() {
      this.modelForm = emptyForm()
      if (this.$refs.modelForm) {
        this.$refs.modelForm.resetFields()
      }
    },
    saveModel() {
      this.$refs.modelForm.validate(valid => {
        if (!valid) return
        const payload = {
          ...this.modelForm,
          statusLabel: this.getStatusLabel(this.modelForm.status),
          updatedAt: new Date().toISOString().slice(0, 10)
        }
        if (this.modelForm.id) {
          const idx = this.allModels.findIndex(item => item.id === this.modelForm.id)
          if (idx >= 0) {
            this.allModels.splice(idx, 1, payload)
          }
          this.$message.success('保存成功')
        } else {
          payload.id = `model-${this.nextId++}`
          payload.nodeCount = 0
          this.allModels.unshift(payload)
          this.$message.success('新建成功')
        }
        this.dialogVisible = false
        this.applyFilters()
      })
    },
    handleDelete(row) {
      this.$confirm(`确认删除模型「${row.name}」？`, '提示', { type: 'warning' })
        .then(() => {
          this.allModels = this.allModels.filter(item => item.id !== row.id)
          this.applyFilters()
          this.$message.success('删除成功')
        })
        .catch(() => {})
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-header {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-toolbar {
  margin-bottom: 12px;
}
</style>
